/**
 * signReadiness — the single source of truth for "can the patient sign?".
 *
 * Extracted from the review-and-sign panel (BridgeFieldsForm.jsx) so the gate
 * logic is pure, unit-testable, and shared — the questionnaire and the panel
 * used to disagree on what "required" means for a missing key, which trapped
 * patients behind phantom-required fields ("I filled the form but it won't let
 * me sign"). One function now decides it for both the UI and the test harness.
 *
 * Given the dispatched fields-schema + the patient's current answers, it
 * computes which fields actually apply (audience + conditional + Yes/No-pair),
 * which of those are genuinely required, which are still blocking, and whether
 * the signature gate is satisfied.
 *
 * Required semantics: a field is required ONLY when the model marks it
 * `required === true`. A missing key means optional (matches
 * bridgeQuestionnaire.js). And when the schema isn't modeled at all
 * (schemaIsModeled === false — a stale/partial dispatch), NOTHING hard-blocks
 * the signature: the panel is review-and-sign.
 */

import type { SchemaField } from "../types/shared";
import schemaIsModeled from "./schemaModel";
import { audienceMatcher } from "./signerAudience";
import { FORMATS, formatValue, isFormatValid, inferInputFormat } from "../shared/lib/inputFormats";

const BINARY_OFF = ["false", "no", "off", "0", "unchecked"];

export const truthy = (v: unknown): boolean =>
  v != null &&
  String(v).trim() !== "" &&
  !BINARY_OFF.includes(String(v).toLowerCase());

// Turn a snake_case machine_name into a readable label as a last resort. Field
// names normalize to snake_case at dispatch, so without this the panel showed
// raw `insurance_policy_group_number` text. Prefer the model's own label/stem.
const humanizeName = (s: unknown): string =>
  String(s || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c: string) => c.toUpperCase())
    .trim();

interface NormalizedField {
  name: string;
  label: unknown;
  required: boolean;
  conditionalOn: { trigger?: string; show_when?: string } | null;
  audience: string | null;
  input_format: string | null;
  type: string | null;
  section?: string | null;
}

const displayLabel = (f: any): unknown =>
  f.label || f.stem || f.patient_stem || humanizeName(f.name);

const normalizeField = (f: string | SchemaField): NormalizedField =>
  typeof f === "string"
    ? { name: f, label: humanizeName(f), required: false, conditionalOn: null, audience: null,
        input_format: null, type: null }
    : {
        name: f.name,
        label: displayLabel(f),
        required: f.required === true, // explicit-true only; missing = optional
        conditionalOn: f.conditionalOn || null,
        audience: f.audience || null,
        section: f.section || null, // form's own section label, for panel grouping
        // Carried through so the format gate can validate typed values.
        input_format: f.input_format || null,
        type: (f.type || (f as any).widget) || null,
      };

/**
 * @param {object} args
 * @param {Array} args.schemaFields  the decrypted dispatch schema (objects or strings)
 * @param {object} args.values       current {machine_name: value} answers
 * @param {string|string[]} [args.signerAudience="patient"]  "patient" | "plan_holder",
 *   or BOTH on a single-signer send — see lib/signerAudience.
 * @param {object|null} [args.signature]  {confirmed, name, mode, imageB64} — omit to skip the signature gate
 * @returns {{fields, panelFields, requiredPanel, blocking, formatBlocking,
 *            requiredFilledCount, allRequiredFilled, schemaModeled,
 *            signatureReady, canSubmit}}
 */
export function computeSignReadiness({
  schemaFields = [],
  values = {},
  signerAudience = "patient",
  signature = null,
}: {
  schemaFields?: Array<string | SchemaField>;
  values?: Record<string, unknown>;
  signerAudience?: string | string[];
  signature?: { confirmed?: boolean; name?: string; mode?: string; imageB64?: string } | null;
  // Index signature tolerates excess caller keys (e.g. a stray `audience` — the
  // function reads `signerAudience`; see report note on the test call site).
  [key: string]: unknown;
}) {
  const fields = (Array.isArray(schemaFields) ? schemaFields : []).map(normalizeField);

  const triggerAnswer = (base: string): string => {
    if (truthy(values[`${base}_yes`])) return "yes";
    if (truthy(values[`${base}_no`])) return "no";
    const v = values[base];
    return v != null && String(v).trim() !== "" ? String(v).toLowerCase() : "";
  };
  const fieldVisible = (f: NormalizedField): boolean => {
    const c = f.conditionalOn;
    if (!c || !c.trigger) return true;
    return triggerAnswer(c.trigger) === String(c.show_when ?? "yes").toLowerCase();
  };
  // `signerAudience` is ONE audience or SEVERAL (a single-signer session owns
  // both `patient` and `plan_holder` — lib/signerAudience). Match by
  // MEMBERSHIP, never equality: strict equality here is what would drop the
  // plan-member questions off the only link a self-as-plan-member patient gets.
  const matchesAudience = audienceMatcher(signerAudience);
  const audienceOK = (f: NormalizedField): boolean =>
    !matchesAudience || matchesAudience(f.audience);
  // The unchosen side of a Yes/No pair (the patient picked the other) drops out.
  const ynHiddenCounterpart = (f: NormalizedField): boolean => {
    const m = String(f.name).match(/^(.*)_(yes|no)$/i);
    if (!m) return false;
    const sibling = `${m[1]}_${m[2].toLowerCase() === "yes" ? "no" : "yes"}`;
    return truthy(values[sibling]) && !truthy(values[f.name]);
  };
  const isSignature = (f: NormalizedField): boolean => /signature/i.test(String(f.name));

  const panelFields = fields.filter(
    (f) => audienceOK(f) && fieldVisible(f) && !ynHiddenCounterpart(f) && !isSignature(f)
  );

  // Stale/partial schema → nothing hard-blocks the signature.
  const schemaModeled = schemaIsModeled(schemaFields);
  const fieldRequired = (f: NormalizedField): boolean => (schemaModeled ? !!f.required : false);

  const isFilled = (f: NormalizedField): boolean =>
    values[f.name] != null && String(values[f.name]).trim() !== "";

  const requiredPanel = panelFields.filter(fieldRequired);
  const requiredFilledCount = requiredPanel.filter(isFilled).length;
  const blocking = requiredPanel.filter((f) => !isFilled(f)).map((f) => f.name);
  const allRequiredFilled = requiredPanel.every(isFilled);

  // Format gate: a wired-format field (DOB/phone/postal/email/health card) that
  // the patient FILLED with a malformed value blocks Sign/Submit — a bad phone
  // or date shouldn't fax out. Empty is never a format block (required-ness is
  // separate, handled above). health_card_ns is excluded: out-of-province cards
  // don't match the MSI 10-digit shape, so they warn but never block. This runs
  // over the visible panelFields, so conditional-inactive / unchosen Yes/No
  // fields can't trap the patient.
  const formatFieldKey = (f: NormalizedField): string | null => {
    const key = f.input_format && FORMATS[f.input_format] ? f.input_format : inferInputFormat(f);
    return key && FORMATS[key] ? key : null;
  };
  const formatBlocking = panelFields
    .filter((f) => {
      const key = formatFieldKey(f);
      if (!key || key === "health_card_ns") return false;
      const v = values[f.name];
      if (v == null || String(v).trim() === "") return false; // empty ≠ blocking
      // Validate what the patient SEES, not the raw stored value: an
      // auto-filled "B3H1A1" renders "B3H 1A1" and is valid, yet blocked
      // Sign/Submit with nothing on screen to fix (case imp_563aea68 — the
      // display-vs-validate split PR #337 fixed on the confirm screen).
      return !isFormatValid(key, formatValue(key, v));
    })
    .map((f) => f.name);

  let signatureReady: boolean | null = null;
  let canSubmit: boolean | null = null;
  if (signature) {
    const hasMark = signature.mode === "draw" ? !!signature.imageB64 : true;
    signatureReady =
      !!signature.confirmed &&
      String(signature.name || "").trim().length > 0 &&
      hasMark;
    canSubmit = allRequiredFilled && formatBlocking.length === 0 && signatureReady;
  }

  return {
    fields,
    panelFields,
    requiredPanel,
    blocking,
    formatBlocking,
    requiredFilledCount,
    allRequiredFilled,
    schemaModeled,
    signatureReady,
    canSubmit,
  };
}

export default computeSignReadiness;
